import { useEffect, useState, type FormEvent } from "react";
import { api } from "../../api/apiClient";
import type { CatalogItem, Location, ReceivedBatchMoney, TypedMoneyBasis } from "../../api/types";
import {
  formatUnitPrice,
  invoiceTotalFromUnitPrice,
  parsePositiveAmount,
  priceQuantity,
  unitPriceFromInvoiceTotal,
  usableStemCost,
} from "../../utils/receiveTotals";
import { movesByBatch } from "../../utils/movesByBatch";
import { IVA_TAKEN_OUT_HINT, ivaBasisOptions, PICK_IVA_BASIS } from "../../utils/purchaseMoneyText";
import { receivePriceSuggestion } from "../../utils/receivePriceSuggestion";
import { basisAfterEdit, basisForPrefill } from "../../utils/typedMoneyBasis";
import { Modal } from "../Modal";
import { SegmentedControl } from "../ui/SegmentedControl";
import { ReceiveItemFinder } from "./ReceiveItemFinder";

/**
 * G-94, OD-58 — ONE door for receiving, whatever arrived.
 *
 * Until now there were two: "Receive flowers" on the shop page's toolbar, and a
 * per-row "Receive" G-91 added for everything else, whose tooltip had to say
 * *"Perishable and flower items: use Receive flowers below"*. That sentence is
 * the defect: the server already knows which path an item takes, and expressing
 * that knowledge as a refusal makes the person carry the rule.
 *
 * So the modal asks for the ITEM first and then shows the fields that item
 * needs. `movesByBatch` — the frontend's single copy of the server's own
 * sentence — decides which.
 *
 * **The two paths are genuinely different, and the difference is the server's,
 * not a style choice:**
 *
 * | | batch items (flowers, perishables) | everything else |
 * |---|---|---|
 * | endpoint | `receive-flower-batch` | `receive-single-use` |
 * | supplier | **required** | not asked for |
 * | quantity | invoiced AND usable (the gap is receiving waste) | one number |
 * | price | **required** (`ReceiveFlowerBatchDto.invoiceTotalEur` has no `@IsOptional`) | optional since G-86 |
 *
 * That last row is the one to be careful with. G-86 and G-91 established that a
 * price nobody knows yet is left blank rather than invented as €0 — but only on
 * the single-use path. Blanking it on a flower batch would post a 400. So the
 * money box is required on one path and optional on the other, and says which
 * it is. Making them uniform would mean either lying to the server or undoing
 * the honest-unknown rule.
 *
 * **G-121 S3 — which side of IVA (OD-138).** Once the money box holds an
 * amount, a "Before IVA / Includes IVA {rate} %" switch appears under it with
 * nothing picked, and saving waits for a pick: the biggest figure on a
 * Spanish delivery note is the total WITH IVA, so no default is safe. The
 * Total / Per unit toggle is quantity arithmetic and keeps the side; the
 * server takes the IVA out, and the success line shows its figures.
 */

export type ReceivePreset = {
  itemId: string;
  itemName?: string;
  zoneId?: string;
  zoneName?: string;
} | null;

function nowLocalIsoMinute(): string {
  const now = new Date();
  const offsetMs = now.getTimezoneOffset() * 60_000;
  return new Date(now.getTime() - offsetMs).toISOString().slice(0, 16);
}

export function ReceiveModal({
  locationId,
  zones,
  preset,
  stockByItem,
  newItemHref,
  onClose,
  onSuccess,
}: {
  locationId: string;
  zones: Location["zones"];
  preset: ReceivePreset;
  /** On-hand quantity at this shop, by item — shown beside each search result. */
  stockByItem?: ReadonlyMap<string, number>;
  /** "New item" from an empty search; absent for people who cannot create items. */
  newItemHref?: (seed?: string) => string;
  onClose: () => void;
  /** G-121 S3: the stored batch, so the page can show the server's split. */
  onSuccess: (received: { itemName: string; batch: ReceivedBatchMoney }) => void;
}) {
  /** G-83's create-then-receive lands here knowing both, so it skips the picker. */
  const compact = Boolean(preset?.itemId && preset.zoneId);

  const [itemId, setItemId] = useState(preset?.itemId ?? "");
  const [item, setItem] = useState<CatalogItem | null>(null);
  const [itemError, setItemError] = useState<string | null>(null);

  const [form, setForm] = useState(() => ({
    // Owner, 2026-10-10: most deliveries go to the front of the shop, so the
    // area opens on Front shop (when the shop has one); it can still be changed.
    zoneId: preset?.zoneId ?? zones.find((z) => z.zoneType === "FRONT_SHOP")?.id ?? "",
    quantity: "",
    invoicedQty: "",
    usableQty: "",
    supplierId: "",
    purchasedAt: nowLocalIsoMinute(),
    receivedAt: nowLocalIsoMinute(),
    invoiceTotalEur: "",
    invoiceNumber: "",
  }));
  const [suppliers, setSuppliers] = useState<{ id: string; name: string; code: string }[]>([]);
  const [submitting, setSubmitting] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);
  /** Per unit first (owner, 2026-10-09): stickers and the one-tap suggestion are per unit. */
  const [moneyMode, setMoneyMode] = useState<"total" | "perUnit">("perUnit");
  const [unitPriceEur, setUnitPriceEur] = useState("");
  /** G-121 S3: no side until the person picks one (OD-138). */
  const [moneyBasis, setMoneyBasis] = useState<TypedMoneyBasis | null>(null);
  const [paperworkOpen, setPaperworkOpen] = useState(false);

  /**
   * The picked item decides the form, so the form waits for it. Resolved by id
   * rather than handed over by the picker, because a preset arrives as an id
   * too (from the item card, or from G-83's redirect) and one mechanism that
   * covers both beats two that each cover half.
   */
  useEffect(() => {
    // A different item may have a different IVA rate: its side is picked anew.
    setMoneyBasis(null);
    setItem(null);
    setForm((f) => ({ ...f, supplierId: "" }));
    if (!itemId) {
      setItem(null);
      setItemError(null);
      return;
    }
    let cancelled = false;
    setItemError(null);
    api
      .getItem(itemId)
      .then((next) => {
        if (!cancelled) {
          setItem(next);
          setForm((f) => ({ ...f, supplierId: next.lastSupplierId ?? "" }));
        }
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        setItem(null);
        setItemError(err instanceof Error ? err.message : "Could not load that item");
      });
    return () => {
      cancelled = true;
    };
  }, [itemId]);

  const byBatch = item ? movesByBatch(item) : false;
  const priceSuggestion = item ? receivePriceSuggestion(item) : null;

  useEffect(() => {
    if (!byBatch) return;
    let cancelled = false;
    void api
      .listSuppliers()
      .then((rows) => {
        if (!cancelled) setSuppliers(rows);
      })
      .catch(() => {
        if (!cancelled) setSuppliers([]);
      });
    return () => {
      cancelled = true;
    };
  }, [byBatch]);

  /** The quantity a price is quoted for — invoiced stems on a batch (D-54). */
  const moneyQuantity = priceQuantity({
    byBatch,
    quantity: Number(form.quantity),
    invoicedQty: Number(form.invoicedQty),
  });
  const usableCostNote = (total: number): string => {
    if (!byBatch) return "";
    const usable = Number(form.usableQty);
    const perUsable = usableStemCost(total, moneyQuantity, usable);
    return perUsable === null
      ? ""
      : ` With ${moneyQuantity - usable} unusable, each usable stem costs €${formatUnitPrice(perUsable)}.`;
  };

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!item) return;
    setSubmitting(true);
    setFormError(null);
    try {
      const typedMoney =
        moneyMode === "total"
          ? parsePositiveAmount(form.invoiceTotalEur)
          : parsePositiveAmount(unitPriceEur);
      if (typedMoney === "invalid") {
        throw new Error(
          moneyMode === "total"
            ? "Invoice total must be zero or greater"
            : "Price per unit must be zero or greater",
        );
      }
      if (typedMoney !== null && moneyBasis === null) throw new Error(PICK_IVA_BASIS);

      if (byBatch) {
        const invoicedQty = Number(form.invoicedQty);
        const usableQty = Number(form.usableQty);
        if (!Number.isFinite(invoicedQty) || invoicedQty <= 0) {
          throw new Error("Invoiced quantity must be greater than zero");
        }
        if (!Number.isFinite(usableQty) || usableQty <= 0) {
          throw new Error("Usable quantity must be greater than zero");
        }
        if (usableQty > invoicedQty) {
          throw new Error("Usable quantity cannot exceed invoiced quantity");
        }
        /**
         * The batch endpoint requires a total, so the honest-unknown rule that
         * governs the other path cannot apply here: say so rather than posting
         * a 0 and asserting the flowers were free.
         */
        if (typedMoney === null) {
          throw new Error("A flower batch needs its invoice total — the cost is spread across the stems");
        }
        // G-121 S3 (OD-138): never a default side — refused here, not assumed.
        const batchBasis = moneyBasis;
        if (batchBasis === null) throw new Error(PICK_IVA_BASIS);
        const invoiceTotalEur =
          moneyMode === "total"
            ? typedMoney
            : invoiceTotalFromUnitPrice(
                typedMoney,
                priceQuantity({ byBatch: true, quantity: usableQty, invoicedQty }),
              );
        const batch = await api.receiveFlowerBatch({
          itemId,
          supplierId: form.supplierId,
          locationId,
          zoneId: form.zoneId,
          invoicedQty,
          usableQty,
          invoiceTotalEur,
          moneyBasis: batchBasis,
          invoiceNumber: form.invoiceNumber.trim() || undefined,
          purchasedAt: new Date(form.purchasedAt).toISOString(),
          receivedAt: new Date(form.receivedAt).toISOString(),
        });
        onSuccess({ itemName: item.name, batch });
        return;
      }

      const quantity = Number(form.quantity);
      if (!Number.isFinite(quantity) || quantity <= 0) {
        throw new Error("Quantity must be greater than zero");
      }
      /**
       * G-86/G-91: a blank price is SENT AS NOTHING, never as 0. The server
       * seeds the unit cost from the item's last real batch or the catalogue
       * default and books zero spend; a 0 would instead assert the goods were
       * free and price every bouquet containing them at a phantom margin.
       */
      const invoiceTotalEur =
        typedMoney === null
          ? undefined
          : moneyMode === "total"
            ? typedMoney
            : invoiceTotalFromUnitPrice(typedMoney, quantity);
      let purchasedAtIso: string;
      let receivedAtIso: string;
      if (compact) {
        const anchor = nowLocalIsoMinute();
        purchasedAtIso = new Date(anchor).toISOString();
        receivedAtIso = purchasedAtIso;
      } else {
        purchasedAtIso = new Date(form.purchasedAt).toISOString();
        receivedAtIso = new Date(form.receivedAt).toISOString();
      }
      const batch = await api.receiveStock({
        itemId,
        locationId,
        zoneId: form.zoneId,
        quantity,
        purchasedAt: purchasedAtIso,
        receivedAt: receivedAtIso,
        ...(invoiceTotalEur === undefined || moneyBasis === null
          ? {}
          : { invoiceTotalEur, moneyBasis }),
        invoiceNumber: compact ? undefined : form.invoiceNumber.trim() || undefined,
      });
      onSuccess({ itemName: item.name, batch });
    } catch (err: unknown) {
      setFormError(err instanceof Error ? err.message : "Could not receive stock");
    } finally {
      setSubmitting(false);
    }
  };

  /**
   * G-91: carry the money across when the toggle flips instead of leaving it
   * behind — blanking it silently would let a receipt post at no cost.
   */
  function switchMoneyMode(next: "total" | "perUnit") {
    if (next === moneyMode) return;
    const convertible = Number.isFinite(moneyQuantity) && moneyQuantity > 0;
    if (next === "perUnit") {
      const total = parsePositiveAmount(form.invoiceTotalEur);
      const unit =
        total === null || total === "invalid" || !convertible
          ? null
          : unitPriceFromInvoiceTotal(total, moneyQuantity);
      /**
       * When the conversion cannot be done — no quantity typed yet — the other
       * box is LEFT ALONE rather than blanked. Clearing it discarded a figure
       * the person had already entered: type 12 as a total before the quantity,
       * tap Per unit, tap Total, and the 12 was gone. Found by /code-review on
       * this branch; it is the same blanking G-91 had to fix once already, and
       * on the batch path the total is mandatory, so losing it is worse.
       */
      if (unit !== null) setUnitPriceEur(String(Number(unit.toFixed(4))));
    } else {
      const unit = parsePositiveAmount(unitPriceEur);
      const total =
        unit === null || unit === "invalid" || !convertible
          ? null
          : invoiceTotalFromUnitPrice(unit, moneyQuantity);
      if (total !== null) setForm((f) => ({ ...f, invoiceTotalEur: String(total) }));
    }
    setMoneyMode(next);
  }

  /** The active money box's text — what the IVA switch qualifies. */
  const activeMoney = moneyMode === "total" ? form.invoiceTotalEur : unitPriceEur;

  /** The figure the other box would hold, worked out as you type. */
  const moneyEcho = (() => {
    const nothingTyped =
      (moneyMode === "total" ? form.invoiceTotalEur : unitPriceEur).trim() === "";
    if (nothingTyped) {
      return byBatch
        ? "A flower batch needs its total: the cost is spread across the usable stems."
        : "No price: the usual cost for this item is used, and nothing is booked as spend. You can fill it in from the invoice later.";
    }
    if (!Number.isFinite(moneyQuantity) || moneyQuantity <= 0) {
      return byBatch
        ? "Enter the invoiced quantity, and the other figure is worked out here."
        : "Enter how many arrived, and the other figure is worked out here.";
    }
    if (moneyMode === "total") {
      const total = parsePositiveAmount(form.invoiceTotalEur);
      if (total === null || total === "invalid") return "The total on the delivery note.";
      const unit = unitPriceFromInvoiceTotal(total, moneyQuantity);
      return unit === null
        ? "The total on the delivery note."
        : `That is €${formatUnitPrice(unit)} each${
            byBatch ? " as invoiced" : ""
          }.${usableCostNote(total)}`;
    }
    const unit = parsePositiveAmount(unitPriceEur);
    if (unit === null || unit === "invalid") return "The price on the sticker or the box.";
    const total = invoiceTotalFromUnitPrice(unit, moneyQuantity);
    return `${moneyQuantity} × that is €${total.toFixed(2)} in total.${usableCostNote(total)}`;
  })();

  const canSubmit =
    Boolean(item) &&
    (activeMoney.trim() === "" || moneyBasis !== null) &&
    form.zoneId &&
    (byBatch
      ? form.supplierId && form.invoicedQty && form.usableQty && form.purchasedAt && form.receivedAt
      : form.quantity && (compact || (form.purchasedAt && form.receivedAt)));

  const title = compact ? `Receive — ${preset?.itemName ?? "item"}` : "Receive stock";

  return (
    <Modal title={title} onClose={onClose}>
      <p className="recv-intro muted">
        {compact ? (
          <>
            <strong>{preset?.itemName}</strong>
            {" · "}
            {preset?.zoneName}. The shop and area are fixed; the receipt is recorded under your
            account.
          </>
        ) : (
          "Find what arrived — search, scan or pick a recent one."
        )}
      </p>
      <form className="modal-form" onSubmit={handleSubmit}>
        {formError ? <div className="alert error full">{formError}</div> : null}
        {itemError ? <div className="alert error full">{itemError}</div> : null}

        {!compact ? (
          <>
            {/* Not a `.field`: that class restyles every input and select inside
                it, and the finder brings its own search box and chip select. */}
            <div className="full">
              {/* The chips only narrow the LIST; the item picked still decides
                  which form follows, as before. */}
              <ReceiveItemFinder
                locationId={locationId}
                selected={item}
                onSelect={setItemId}
                stockByItem={stockByItem}
                newItemHref={newItemHref}
              />
            </div>
            <div className="field">
              <label htmlFor="recvZone">Area *</label>
              <select
                id="recvZone"
                required
                value={form.zoneId}
                onChange={(e) => setForm((f) => ({ ...f, zoneId: e.target.value }))}
              >
                <option value="">Select an area</option>
                {zones.map((z) => (
                  <option key={z.id} value={z.id}>
                    {z.name} ({z.zoneType})
                  </option>
                ))}
              </select>
            </div>
          </>
        ) : null}

        {item && byBatch ? (
          <>
            <div className="field">
              <label htmlFor="recvSupplier">Supplier *</label>
              <select
                id="recvSupplier"
                required
                value={form.supplierId}
                onChange={(e) => setForm((f) => ({ ...f, supplierId: e.target.value }))}
              >
                <option value="">Select supplier</option>
                {suppliers.map((s) => (
                  <option key={s.id} value={s.id}>
                    {s.name} ({s.code})
                  </option>
                ))}
              </select>
            </div>
            <div className="field">
              <label htmlFor="recvInvoiced">Invoiced quantity *</label>
              <input
                id="recvInvoiced"
                type="number"
                min={0.001}
                step="0.001"
                required
                value={form.invoicedQty}
                onChange={(e) => setForm((f) => ({ ...f, invoicedQty: e.target.value }))}
              />
              <small className="field-hint">What the delivery note says arrived.</small>
            </div>
            <div className="field">
              <label htmlFor="recvUsable">Usable quantity *</label>
              <input
                id="recvUsable"
                type="number"
                min={0.001}
                step="0.001"
                required
                value={form.usableQty}
                onChange={(e) => setForm((f) => ({ ...f, usableQty: e.target.value }))}
              />
              <small className="field-hint">
                What is actually sellable. The gap is recorded as receiving waste.
              </small>
            </div>
          </>
        ) : null}

        {item && !byBatch ? (
          <div className="field">
            <label htmlFor="recvQty">Quantity *</label>
            <input
              id="recvQty"
              type="number"
              min={0.001}
              step="0.001"
              required
              value={form.quantity}
              onChange={(e) => setForm((f) => ({ ...f, quantity: e.target.value }))}
            />
          </div>
        ) : null}

        {item ? (
          <div className="field full">
            <div className="recv-money-head">
              <label htmlFor="recvMoney">{byBatch ? "Price *" : "Price"}</label>
              <div className="recv-money-toggle" role="group" aria-label="How the price is given">
                <button
                  type="button"
                  aria-pressed={moneyMode === "total"}
                  onClick={() => switchMoneyMode("total")}
                >
                  Total
                </button>
                <button
                  type="button"
                  aria-pressed={moneyMode === "perUnit"}
                  onClick={() => switchMoneyMode("perUnit")}
                >
                  Per unit
                </button>
              </div>
            </div>
            {moneyMode === "total" ? (
              <input
                id="recvMoney"
                type="number"
                min={0}
                step="0.01"
                inputMode="decimal"
                required={byBatch}
                placeholder={byBatch ? "From the delivery note" : "Leave empty if you don't know yet"}
                value={form.invoiceTotalEur}
                onChange={(e) => {
                  const next = e.target.value;
                  setForm((f) => ({ ...f, invoiceTotalEur: next }));
                  setMoneyBasis((b) => basisAfterEdit(b, next));
                }}
              />
            ) : (
              <input
                id="recvMoney"
                type="number"
                min={0}
                step="0.0001"
                inputMode="decimal"
                required={byBatch}
                placeholder={byBatch ? "Price per stem" : "Leave empty if you don't know yet"}
                value={unitPriceEur}
                onChange={(e) => {
                  setUnitPriceEur(e.target.value);
                  setMoneyBasis((b) => basisAfterEdit(b, e.target.value));
                }}
              />
            )}
            {/* Owner, 2026-10-09 (option 2): the last price is OFFERED, not
                filled in — an empty box still books nothing as spend, so a
                price only counts once someone tapped or typed it. */}
            {priceSuggestion && activeMoney.trim() === "" ? (
              <button
                type="button"
                className="recv-price-suggest"
                onClick={() => {
                  setMoneyMode("perUnit");
                  setUnitPriceEur(priceSuggestion.unitPriceEur);
                  setMoneyBasis(basisForPrefill(priceSuggestion.unitPriceEur));
                }}
              >
                {priceSuggestion.source === "lastDelivery" ? "Use last price" : "Use usual cost"}{" "}
                <b>€{formatUnitPrice(Number(priceSuggestion.unitPriceEur))}</b> per unit
                <span className="muted"> · before IVA</span>
              </button>
            ) : null}
            {/* G-121 S3: shown once there is an amount to qualify — nothing
                picked until the person picks (OD-138). */}
            {activeMoney.trim() !== "" ? (
              <div className="recv-iva-basis">
                <SegmentedControl
                  options={ivaBasisOptions(item.category.vatRatePercent)}
                  value={moneyBasis}
                  onChange={setMoneyBasis}
                  ariaLabel="Is this amount before IVA or does it include IVA?"
                />
              </div>
            ) : null}
            <p className="field-hint">
              {moneyEcho}
              {activeMoney.trim() === ""
                ? ""
                : moneyBasis === null
                  ? ` ${PICK_IVA_BASIS}`
                  : moneyBasis === "INCLUDES_IVA"
                    ? ` These figures include IVA. ${IVA_TAKEN_OUT_HINT}`
                    : ""}
            </p>
          </div>
        ) : null}

        {item && !compact ? (
          <div className="field full">
            {paperworkOpen || byBatch ? (
              <div className="recv-paperwork">
                <div className="field">
                  <label htmlFor="recvPurchased">Purchased at *</label>
                  <input
                    id="recvPurchased"
                    type="datetime-local"
                    required
                    value={form.purchasedAt}
                    onChange={(e) => setForm((f) => ({ ...f, purchasedAt: e.target.value }))}
                  />
                </div>
                <div className="field">
                  <label htmlFor="recvReceived">Received at *</label>
                  <input
                    id="recvReceived"
                    type="datetime-local"
                    required
                    value={form.receivedAt}
                    onChange={(e) => setForm((f) => ({ ...f, receivedAt: e.target.value }))}
                  />
                </div>
                <div className="field">
                  <label htmlFor="recvInvNo">Invoice number</label>
                  <input
                    id="recvInvNo"
                    type="text"
                    maxLength={64}
                    value={form.invoiceNumber}
                    onChange={(e) => setForm((f) => ({ ...f, invoiceNumber: e.target.value }))}
                  />
                </div>
              </div>
            ) : (
              /* Both dates are already "now", which is right when the goods are
                 on the counter. A flower batch keeps them open: it is invoice
                 paperwork by nature, and the dates drive FIFO. */
              <button
                type="button"
                className="button tinted recv-paperwork-toggle"
                onClick={() => setPaperworkOpen(true)}
              >
                Add invoice details
              </button>
            )}
          </div>
        ) : null}

        <div className="recv-actions full">
          <button className="button" type="submit" disabled={submitting || !canSubmit}>
            {submitting ? "Recording..." : byBatch ? "Receive batch" : "Record receipt"}
          </button>
        </div>
      </form>
    </Modal>
  );
}
